import { useEffect, useId, useRef } from "react";

// It might look unusual to use a global for this, but it avoids having react state
// that would re-render the entire app on navBlocker changes.
// Since `isNavigationBlocked` is only read in an event listener, not in the UI,
// it can be non-reactive.
// In case we need to highlight the blocking state in the UI, we can always use a `useSyncExternalStore`
// to subscribe to changes in the map.
const navBlockMap = new Map<string, boolean | (() => boolean)>();

export function isNavigationBlocked(): boolean {
    for (const block of navBlockMap.values()) {
        const shouldBlock = typeof block === "function" ? block() : block;
        if (shouldBlock) {
            return true;
        }
    }
    return false;
}
export function useNavigationBlocker(block: boolean | (() => boolean)) {
    const id = useId();

    useEffect(() => {
        navBlockMap.set(id, block);
        return () => {
            navBlockMap.delete(id);
        };
    }, [id, block]);
    useBeforeUnload((event) => {
        const shouldBlock = typeof block === "function" ? block() : block;
        if (shouldBlock) {
            event.preventDefault();
            // even though it's deprecated, it's still the only way to block navigating away
            event.returnValue = true;
        }
    });
}

function useBeforeUnload(callback: (event: Event) => void) {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        const onBeforeUnload = (event: Event) => callbackRef.current(event);
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, []);
}
